import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import socketService from '../services/socketService';
import apiService from '../services/apiService';

export default function CallScreen() {
  const { targetUserId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isVideo = false, isCaller = true, offerSdp = null } = location.state || {};

  const [peerName, setPeerName] = useState('');

  const [callState, setCallState] = useState(isCaller ? 'calling' : 'ringing');
  const [micEnabled, setMicEnabled] = useState(true);
  const [speakerOn, setSpeakerOn] = useState(true);
  const [hasLocalVideo, setHasLocalVideo] = useState(false);
  const [hasRemoteVideo, setHasRemoteVideo] = useState(false);
  const [remoteReady, setRemoteReady] = useState(false);
  const [permissionsGranted, setPermissionsGranted] = useState(false);
  const [error, setError] = useState(null);
  const [offlineHint, setOfflineHint] = useState(false);
  const [beeRinging, setBeeRinging] = useState(false);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const beeAudioRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const endCalledRef = useRef(false);
  const pendingCandidatesRef = useRef([]);
  const ringTimeoutRef = useRef(null);
  const ringDelayRef = useRef(null);
  const callStateRef = useRef(callState);

  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  const iceServers = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ],
  };

  const cleanup = () => {
    if (endCalledRef.current) return;
    endCalledRef.current = true;

    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }
    if (ringDelayRef.current) {
      clearTimeout(ringDelayRef.current);
      ringDelayRef.current = null;
    }

    if (beeAudioRef.current) {
      beeAudioRef.current.pause();
      beeAudioRef.current.currentTime = 0;
    }

    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;

    socketService.off('call:answer');
    socketService.clearIceCandidateHandler();
  };

  const endCall = () => {
    cleanup();
    socketService.endCall(targetUserId);
    goBack();
  };

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/', { replace: true });
    }
  };

  const toggleMic = () => {
    const next = !micEnabled;
    setMicEnabled(next);
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach((t) => {
        t.enabled = next;
      });
    }
  };

  const toggleSpeaker = async () => {
    const next = !speakerOn;
    setSpeakerOn(next);
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const outputs = devices.filter((d) => d.kind === 'audiooutput');
      const target = next
        ? outputs.find((d) => /speaker|Speakerphone|speakerphone/i.test(d.label)) || outputs[0]
        : null;
      const el = isVideo ? remoteVideoRef.current : remoteAudioRef.current;
      if (el && typeof el.setSinkId === 'function') {
        await el.setSinkId(target ? target.deviceId : '');
      }
    } catch (e) {
      console.warn('Speaker switch not supported on this browser:', e);
    }
  };

  useEffect(() => {
    apiService
      .getUser(targetUserId)
      .then((u) => setPeerName(u?.displayName || u?.username || ''))
      .catch(() => {});
  }, [targetUserId]);

  useEffect(() => {
    async function requestPermissions() {
      try {
        const constraints = { audio: true };
        if (isVideo) constraints.video = { facingMode: 'user', width: { ideal: 360 }, height: { ideal: 640 } };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        localStreamRef.current = stream;
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
        setHasLocalVideo(isVideo);
        setPermissionsGranted(true);
      } catch (err) {
        setError('Camera and microphone permissions required');
        setPermissionsGranted(false);
      }
    }
    requestPermissions();

    return () => {
      cleanup();
    };
  }, []);

  useEffect(() => {
    if (!permissionsGranted) return;

    async function initCall() {      pcRef.current = new RTCPeerConnection(iceServers);

      pcRef.current.onicecandidate = (e) => {
        if (e.candidate) {
          socketService.sendIceCandidate(targetUserId, e.candidate.toJSON());
        }
      };

      pcRef.current.ontrack = (e) => {
        remoteStreamRef.current = e.streams[0];
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = e.streams[0];
        }
        setHasRemoteVideo(isVideo);
        setRemoteReady(true);
        setCallState('connected');
        if (beeAudioRef.current) {
          beeAudioRef.current.pause();
          beeAudioRef.current.currentTime = 0;
        }
        setBeeRinging(false);
      };

      pcRef.current.oniceconnectionstatechange = () => {
        if (
          pcRef.current?.iceConnectionState === 'disconnected' ||
          pcRef.current?.iceConnectionState === 'failed'
        ) {
          cleanup();
          goBack();
        }
      };

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => {
          pcRef.current.addTrack(track, localStreamRef.current);
        });
      }

      const flushCandidates = async () => {
        if (!pcRef.current) return;
        while (pendingCandidatesRef.current.length) {
          const candidate = pendingCandidatesRef.current.shift();
          try {
            await pcRef.current.addIceCandidate(candidate);
          } catch (err) {
            console.error('Failed to add buffered ICE candidate:', err);
          }
        }
      };

      socketService.setIceCandidateHandler(async (data) => {
        if (!data.candidate) return;
        if (!pcRef.current || !pcRef.current.remoteDescription) {
          pendingCandidatesRef.current.push(data.candidate);
          return;
        }
        try {
          await pcRef.current.addIceCandidate(data.candidate);
        } catch (err) {
          console.error('Failed to add ICE candidate:', err);
        }
      });

      const stopCall = () => {
        cleanup();
        socketService.clearIceCandidateHandler();
        socketService.off('call:end');
        socketService.off('call:answer');
        socketService.off('call:offline');
        socketService.off('call:ringing');
        socketService.off('error');
        goBack();
      };

      socketService.on('call:end', () => {
        if (endCalledRef.current) return;
        stopCall();
      });

      socketService.on('call:offline', () => {
        setOfflineHint(true);
      });

      socketService.on('call:ringing', () => {
        if (ringDelayRef.current) return;
        ringDelayRef.current = setTimeout(() => {
          setBeeRinging(true);
          if (beeAudioRef.current) {
            beeAudioRef.current.currentTime = 0;
            beeAudioRef.current.play().catch(() => {});
          }
        }, 1200);
      });

      if (isCaller) {
        socketService.on('error', (err) => {
          const msg = typeof err === 'object' && err ? err.message || '' : '';
          if (/already|could not|invalid/i.test(msg)) stopCall();
        });

        ringTimeoutRef.current = setTimeout(() => {
          if (callStateRef.current !== 'connected') {
            socketService.endCall(targetUserId);
            stopCall();
          }
        }, 45000);
      }

      if (isCaller) {
        const offer = await pcRef.current.createOffer();
        await pcRef.current.setLocalDescription(offer);
        socketService.sendCallOffer(targetUserId, offer.sdp, isVideo);

        socketService.on('call:answer', async (data) => {
          if (data.sdp && pcRef.current) {
            const answerDesc =
              typeof data.sdp === 'string' ? { type: 'answer', sdp: data.sdp } : data.sdp;
            await pcRef.current.setRemoteDescription(answerDesc);
            await flushCandidates();
            setCallState('connected');
          }
        });
      } else if (offerSdp) {
        const remoteOffer =
          offerSdp && offerSdp.sdp
            ? { type: 'offer', sdp: offerSdp.sdp }
            : { type: 'offer', sdp: offerSdp };
        await pcRef.current.setRemoteDescription(remoteOffer);
        await flushCandidates();
        const answer = await pcRef.current.createAnswer();
        await pcRef.current.setLocalDescription(answer);
        socketService.sendCallAnswer(targetUserId, answer.sdp);
      }
    }

    initCall();
  }, [permissionsGranted]);

  useEffect(() => {
    if (localVideoRef.current && localStreamRef.current) {
      localVideoRef.current.srcObject = localStreamRef.current;
    }
  }, [permissionsGranted, hasLocalVideo]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStreamRef.current && isVideo) {
      remoteVideoRef.current.srcObject = remoteStreamRef.current;
    }
    if (remoteAudioRef.current && remoteStreamRef.current && !isVideo) {
      remoteAudioRef.current.srcObject = remoteStreamRef.current;
      remoteAudioRef.current
        .play()
        .then(() => {
          remoteAudioRef.current.volume = 1.0;
        })
        .catch((err) => {
          console.warn('Remote audio play blocked:', err);
        });
    }
  }, [permissionsGranted, hasRemoteVideo, isVideo, remoteReady]);

  if (!permissionsGranted) {
    return (
      <div className="call-screen">
        <div className="call-container">
          <div className="call-error">{error || 'Permissions required'}</div>
          <button className="auth-button" onClick={() => window.location.reload()}>
            Grant Permissions
          </button>
        </div>
      </div>
    );
  }

  const offlineSuffix = offlineHint ? ' (offline - they will be notified)' : '';
  const statusText =
    callState === 'connected'
      ? `Connected with ${peerName}`
      : isCaller
        ? beeRinging
          ? `wiz wiz to ${peerName}${offlineSuffix}`
          : `on my way to ${peerName}${offlineSuffix}`
        : `${peerName} is calling...`;

  return (
    <div className="call-screen">
      <div className="call-container">
        <audio ref={remoteAudioRef} autoPlay playsInline className="remote-audio" />
        <audio ref={beeAudioRef} src={`${process.env.PUBLIC_URL}/wiz_comedy_5s.wav`} loop preload="auto" />
        {isCaller && callState !== 'connected' && (
          <div className={`bee-fly ${beeRinging ? 'bee-buzz' : ''}`}>🐝</div>
        )}
        {hasRemoteVideo && remoteVideoRef ? (
          <video ref={remoteVideoRef} autoPlay playsInline className="remote-video" />
        ) : (
          <div className="call-placeholder">
            <span className="call-icon">📞</span>
          </div>
        )}

        <div className="call-status">{statusText}</div>

        {isVideo && hasLocalVideo && (
          <div className="local-video-container">
            <video ref={localVideoRef} autoPlay playsInline muted className="local-video" />
          </div>
        )}

        <div className="call-controls">
          <button className="call-control-btn" onClick={toggleMic}>
            {micEnabled ? '🎤' : '🔇'}
          </button>
          <button className="call-control-btn" onClick={toggleSpeaker}>
            {speakerOn ? '🔊' : '🔈'}
          </button>
          <button className="call-control-btn end-call" onClick={endCall}>
            📞
          </button>
        </div>
      </div>
    </div>
  );
}
